import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useLocation } from "react-router";
import queryString from "query-string";
import {
  searchCompaniesAction,
  resetSearchAction,
} from "../actions/search.action";

const PAGE_SIZE = 20;

// Pulled out of the incoming filters object (e.g. from the agent's
// navigate_to_company_search action, or the filters-to-text/text-to-filters
// endpoints) since brand_advertiser_filters is tracked as its own piece of
// state rather than living inside dataForm.
export function splitIncomingFilters(filters = {}) {
  const { brand_advertiser_filters: brandAdvertiserFilters, ...dataForm } =
    filters;
  return { dataForm, brandAdvertiserFilters };
}

export const useCompanySearch = (initialPageSize = PAGE_SIZE) => {
  const dispatch = useDispatch();
  const location = useLocation();

  // Filters can arrive via router state (e.g. pushed by the agent's
  // navigate_to_company_search action) instead of a URL query string, since
  // several filters (financial_filters, nielsen_filters, ...) are nested
  // arrays/objects that don't serialize cleanly into a query string.
  const initialFilters = splitIncomingFilters(location.state?.filters);
  const [dataForm, setDataForm] = useState(initialFilters.dataForm);
  const [filters, setFilters] = useState({});
  const [order, setOrder] = useState("name");
  const [orderDir, setOrderDir] = useState("asc");
  const [pageSize, setPageSize] = useState(initialPageSize);
  const [pageNum, setPageNum] = useState(1);
  const [city, setCity] = useState("");
  const [brandAdvertiserFilters, setBrandAdvertiserFilters] = useState(
    initialFilters.brandAdvertiserFilters || [],
  );
  // Company id -> display name for the "Name" filter (dataForm.id holds only
  // opaque company ids — there's no server-side registry to resolve them
  // back to names, so the autocomplete's own selection is the only place
  // that ever knows the name). Kept out of dataForm/activeFilters (it isn't
  // a real filter) and passed separately to filtersToTextApi so the
  // generated description can say "named Acme Corp" instead of a raw id.
  const [companyNameLabels, setCompanyNameLabels] = useState({});

  // Re-apply incoming filters if the agent navigates here again while already
  // on this page (same route, new location.key — a mount-only initializer
  // above wouldn't catch that second navigation).
  const appliedLocationKeyRef = useRef(location.key);
  useEffect(() => {
    if (
      location.state?.filters &&
      location.key !== appliedLocationKeyRef.current
    ) {
      const incoming = splitIncomingFilters(location.state.filters);
      setDataForm(incoming.dataForm);
      setBrandAdvertiserFilters(incoming.brandAdvertiserFilters || []);
      // The agent's filters are the complete, absolute set — not a delta on
      // top of whatever's currently applied — so the separately-tracked city
      // autocomplete value (see getParsedDataForm below) must be cleared too,
      // or a stale typed-in city would silently keep overriding
      // incoming.dataForm.city (or reassert itself even when the agent didn't
      // set a city at all).
      setCity("");
      setCompanyNameLabels({});
    }
    appliedLocationKeyRef.current = location.key;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.key]);

  const results = useSelector((state) => state.search.results.data);
  const loading = useSelector((state) => state.search.results.loading);

  const getParsedDataForm = useCallback(() => {
    const parsedDataForm = {};
    Object.keys(dataForm).forEach((p) => {
      if (Array.isArray(dataForm[p])) {
        if (dataForm[p].length > 0) {
          parsedDataForm[p] = dataForm[p];
        }
      } else if (typeof dataForm[p] === "number") {
        parsedDataForm[p] = dataForm[p];
      } else if (dataForm[p] && dataForm[p].length > 0) {
        parsedDataForm[p] = dataForm[p];
      } else if (dataForm[p] === true) {
        parsedDataForm[p] = dataForm[p];
      }
    });
    if (city) parsedDataForm.city = city;
    return parsedDataForm;
  }, [dataForm, city]);

  const searchRequest = useCallback(
    (overridePageNum) => {
      let dataParams = {};
      const query = queryString.parse(location.search);
      if (query.key && !dataParams.name) dataParams.name = query.key;

      const parsedDataForm = getParsedDataForm();
      dataParams = { ...dataParams, ...parsedDataForm };

      if (order) {
        dataParams.sort_key = order;
        dataParams.sort_direction = orderDir;
      }

      if (brandAdvertiserFilters.length > 0) {
        dataParams.brand_advertiser_filters = brandAdvertiserFilters;
      }

      const currentPageNum =
        overridePageNum !== undefined ? overridePageNum : pageNum;

      dataParams.pageNum = currentPageNum;
      dataParams.limit = pageSize;

      setFilters(dataParams);
      dispatch(searchCompaniesAction({ data: dataParams }));
    },
    [
      location.search,
      getParsedDataForm,
      order,
      orderDir,
      brandAdvertiserFilters,
      pageNum,
      pageSize,
      dispatch,
    ],
  );

  useEffect(() => {
    searchRequest(1);
    setPageNum(1);
  }, [dataForm, city, order, orderDir, brandAdvertiserFilters, pageSize]);

  const handleOrder = (val) => {
    if (val === order) {
      setOrderDir(orderDir === "asc" ? "desc" : "asc");
    } else {
      setOrderDir("asc");
    }
    setOrder(val);
  };

  const resetSearch = () => {
    dispatch(resetSearchAction());
    setDataForm({});
    setCity("");
    setBrandAdvertiserFilters([]);
    setCompanyNameLabels({});
    setPageNum(1);
  };

  // Same shape as the navigate_to_company_search/update_filters tool schemas
  // (parsed dataForm + city, plus brand_advertiser_filters when set) — used by
  // the filters-to-text/text-to-filters summary, kept separate from `filters`
  // since that one also carries paging/sort params meant only for the search request.
  const activeFilters = useMemo(() => {
    const parsed = getParsedDataForm();
    if (brandAdvertiserFilters.length > 0) {
      parsed.brand_advertiser_filters = brandAdvertiserFilters;
    }
    return parsed;
  }, [getParsedDataForm, brandAdvertiserFilters]);

  return {
    dataForm,
    setDataForm,
    filters,
    activeFilters,
    order,
    orderDir,
    pageSize,
    setPageSize,
    pageNum,
    setPageNum,
    city,
    setCity,
    brandAdvertiserFilters,
    setBrandAdvertiserFilters,
    companyNameLabels,
    setCompanyNameLabels,
    results,
    loading,
    searchRequest,
    handleOrder,
    handlePageChange: (page, size) => {
      setPageNum(page);
      if (size !== pageSize) setPageSize(size);
      searchRequest(page);
    },
    resetSearch,
    getParsedDataForm,
  };
};

export default useCompanySearch;
